import React from "react";
import Page from "../../page-layout";
import {
  BackButton,
  NotificationsSearchBarHeader,
  NotificationFilters,
  getNotificationIcon,
  getBadgeColor,
  getActionButtonText,
  getBadgeText,
  formatDateTime,
} from "../../../parhato2/src/entities";
import MaxWidthContainer from "../../../components/UI/MaxWidthContainer/MaxWidthContainer";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "../../../hooks/useSearchParams";
import {
  useInfiniteGetNotificationsQuery,
  useMarkAsReadMutation,
  useGetUnreadCountQuery,
} from "../../../providers/notifications";
import { IonInfiniteScroll, IonInfiniteScrollContent, IonButton, IonIcon } from "@ionic/react";
import { useAuth } from "../../../contexts/auth-context";
import { Browser } from "@capacitor/browser";
import { useHistory } from "react-router-dom";
import cln from "classnames";
import { Notification } from "@core/types";
import PullToRefresh from "../../../parhato2/src/entities/pullToRefresh";
import { useProductDetailSheetStore } from "../../../providers/productDetailSheetStore";

const Notifications = () => {
  const { t } = useTranslation();
  const { searchParams } = useSearchParams();
  const searchQuery = searchParams.get("q") || "";
  const notificationType = searchParams.get("notificationType");
  const { user } = useAuth();
  const customerId = user?.id;
  const isAuthenticated = !!customerId;
  const history = useHistory();

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, refetch, isLoading, isFetching } =
    useInfiniteGetNotificationsQuery({
      params: {
        page: 1,
        perPage: 20,
        type: notificationType,
        search: searchQuery,
        customerId: customerId || undefined,
        isPublicOnly: !isAuthenticated,
      },
    });

  const { data: unreadCountData, refetch: refetchUnreadCount } = useGetUnreadCountQuery({
    params: { customerId: customerId || "" },
    options: {
      enabled: isAuthenticated,
    },
  });

  const markAsReadMutation = useMarkAsReadMutation({
    options: {
      onSuccess: (result: any) => {
        refetch();
        refetchUnreadCount();
      },
    },
  });

  const notifications = data?.pages.flatMap((page: Notification[]) => page || []) || [];
  const unreadCount = unreadCountData?.unreadCount || 0;

  const handleFilterChange = async (type: string | null) => {
    await refetch();
    if (isAuthenticated) {
      refetchUnreadCount();
    }
  };

  const handleMarkAsRead = (notificationId: number) => {
    if (!isAuthenticated || !customerId) return;
    markAsReadMutation.mutate({
      notificationId,
    });
  };

  const handleNotificationClick = (notification: Notification) => {
    if (isAuthenticated && !notification.isRead) {
      handleMarkAsRead(notification.id);
    }

    const url = notification?.url;

    if (url) {
      if (notification.urlType === "OPEN_LINK") {
        Browser.open({ url });
      } else if (notification.urlType === "OPEN_PAGE" && notification.url?.includes("/product-detail-sheet")) {
        const id = notification.url?.split("/product-detail-sheet/").pop() || "";
        useProductDetailSheetStore.getState().open(id);
      } else {
        history.push(url);
      }
      return;
    }
  };

  const doRefresh = async () => {
    if (isAuthenticated) {
      await Promise.all([refetch(), refetchUnreadCount()]);
    } else {
      await refetch();
    }
  };

  const loadMore = async (event: CustomEvent) => {
    if (hasNextPage && !isFetchingNextPage) {
      try {
        await fetchNextPage();
      } catch (error) {
        console.error("Error loading next page:", error);
      }
    }

    requestAnimationFrame(() => {
      (event.target as HTMLIonInfiniteScrollElement).complete();
    });
  };

  return (
    <Page>
      <Page.Header className="h-16 mt-[env(safe-area-inset-top)]">
        <MaxWidthContainer classList="!sticky top-0 z-50 bg-white h-16">
          <div className="w-full flex items-center justify-between h-[4rem] px-2.5 relative">
            <BackButton buttonStyles="border" />
            <div className="absolute left-1/2 transform -translate-x-1/2 text-[1.125rem] font-bold truncate">
              {t("Notifications")} {isAuthenticated && unreadCount > 0 && `(${unreadCount})`}
            </div>
          </div>
        </MaxWidthContainer>
        <NotificationsSearchBarHeader onClick={() => {}} tabIndex={-1} />

        <div className="py-2">
          <div className="flex justify-between items-center gap-2">
            <div className="flex-1 overflow-hidden ml-2">
              <NotificationFilters
                onFilterChange={handleFilterChange}
                unreadCount={unreadCount}
                isAuthenticated={isAuthenticated}
                customerId={customerId}
              />
            </div>
          </div>
        </div>
      </Page.Header>
      <Page.Content className={cln("ion-content-scroll-host", "no-scrollbar")}>
        <PullToRefresh onRefresh={doRefresh} />
        <div
          style={{
            height: "calc(env(safe-area-inset-top) + 130px)",
            minHeight: "140px",
          }}
        ></div>
        {isFetching && !isFetchingNextPage && notifications.length > 0 && (
          <div className="fixed inset-0 bg-white/80 backdrop-blur-sm z-50 flex flex-col items-center justify-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
            <div className="text-gray-500 font-medium">{t("Loading...")}</div>
          </div>
        )}
        <MaxWidthContainer classList="px-2.5 py-4">
          {isLoading || (isFetching && notifications.length === 0) ? (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mb-4"></div>
              <div className="text-gray-500">{t("Loading...")}</div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-gray-500">{t("No notifications")}</div>
            </div>
          ) : (
            <div className="space-y-3">
              {notifications.map((notification: Notification, index: number) => {
                const isUnread = isAuthenticated && !notification.isRead;
                return (
                  <div
                    key={`${notification.id}-${index}`}
                    className={`${isAuthenticated && notification.isRead ? "bg-gray-50" : "bg-white"} rounded-xl p-4 flex gap-2 hover:bg-gray-100 focus:bg-gray-100 cursor-pointer transition-colors duration-200 focus:outline-none`}
                    tabIndex={0}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <IonIcon
                      icon={getNotificationIcon(notification.type)}
                      className="text-[14px] text-gray-600 bg-gray-100 rounded-md p-1"
                    />
                    <div className="flex flex-col flex-1 gap-2">
                      <div className="flex items-center justify-between gap-3 flex-1">
                        <div className="flex-1 min-w-0 pr-4">
                          <h3 className="font-semibold text-gray-900 text-[14px] leading-tight">
                            {notification.title}
                          </h3>
                        </div>
                        {notification.type && (
                          <span
                            className={`text-[11px] px-2 align-center rounded-full font-semibold whitespace-nowrap ${getBadgeColor(notification.type)}`}
                          >
                            {(notification.typeName && notification.typeName.trim()) || getBadgeText(notification.type)}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center justify-between gap-2 align-center">
                        <p className="text-[10px] text-gray-600 leading-relaxed">{notification.body}</p>
                        <p className="text-[8px] text-gray-500 whitespace-nowrap align-center">
                          {formatDateTime(notification.createdAt)}
                        </p>
                      </div>
                      <div className="flex items-center justify-between gap-2 align-center">
                        <IonButton
                          size="small"
                          className="text-[11px] font-medium"
                          style={{
                            "--height": "32px",
                            "--padding-start": "16px",
                            "--padding-end": "16px",
                            "--background": "#22c55e",
                            "--background-hover": "#16a34a",
                            "--background-activated": "#15803d",
                            "--color": "white",
                            "--border-radius": "18px",
                            "--font-weight": "500",
                          }}
                        >
                          {getActionButtonText(notification.type)}
                        </IonButton>
                        {isUnread && <div className="w-2.5 h-2.5 bg-red-500 rounded-full"></div>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </MaxWidthContainer>
        {isFetchingNextPage && (
          <div className="text-center py-4">
            <div className="text-gray-500 text-sm">{t("Loading more...")}</div>
          </div>
        )}
        <IonInfiniteScroll onIonInfinite={loadMore} disabled={!hasNextPage} threshold="100px">
          <IonInfiniteScrollContent loadingText={t("Loading more...")} loadingSpinner="dots" />
        </IonInfiniteScroll>
      </Page.Content>
    </Page>
  );
};

export default Notifications;
